// ─── AutoChinBot Workspace Persistence ─────────────────────────
// Persists the AutoChinBot workspace to Supabase (bot_session_state) so
// the bot can be restored EXACTLY where the user left off, on any device.
//
// Kept structurally identical to the Chin Bot workspace persistence so the
// future functional columns (added in later phases) slot straight in.
//
// Session key: {strategyId}::account::{accountId}   (per cTrader account)
// session_data.type = 'autochinbot'                 (distinct from 'chin')

import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { buildSessionKey } from '@/lib/session-key';
import { getDeviceWriterId } from '@/lib/device-id';

// ─── Types ──────────────────────────────────────────────────

export interface AutoChinSymbolConfig {
  /** Free-text note for the symbol. */
  note?: string;
  /** task1: Big Candle threshold — minimum A0 body (in pips). */
  bigCandlePips?: number;
  /** task3: ST50 threshold — max |SMA50 − TMA50| distance (in pips). Default 5 when unset. */
  st50Pips?: number;
  /**
   * task5: Rule 2 — max concurrent open positions for this symbol.
   * NOT a UI column: delivered through the user↔AutoChinBot Telegram
   * conversation. 0 / undefined = no limit.
   */
  maxOrders?: number;
  // NOTE: future functional-column values (volume, max, direction, ...)
  // will be added here in later phases without a schema change.
}

export interface AutoChinWorkspaceData {
  selectedListId: string;
  selectedAccountId: string;
  /** Per-symbol value for the "Trạng thái" column (free-form for now). */
  statusMap: Record<number, string>;
  symbolConfigs: Record<number, AutoChinSymbolConfig>;
  /** Global pause for all pending orders (task5 blocked a pending fire). */
  pendingOrdersPaused?: boolean;
  /** Reason shown while pending orders are paused. */
  pendingOrdersPauseReason?: string;
}

export type AutoChinSaveStatus = 'idle' | 'syncing' | 'saved' | 'error';

// ─── Keepalive auth cache ──────────────────────────────────────

interface CachedAuth {
  userId: string;
  accessToken: string;
}
let authCache: CachedAuth | null = null;

// ─── Hook ───────────────────────────────────────────────────

export function useAutoChinWorkspacePersistence(
  strategyId: string,
  accountId: string,
  onExternalChange?: (data: AutoChinWorkspaceData) => void,
) {
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const onExternalChangeRef = useRef(onExternalChange);
  onExternalChangeRef.current = onExternalChange;

  const latestDataRef = useRef<AutoChinWorkspaceData | null>(null);

  const [saveStatus, setSaveStatus] = useState<AutoChinSaveStatus>('idle');
  const statusResetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const markSaved = useCallback(() => {
    setSaveStatus('saved');
    if (statusResetTimerRef.current) clearTimeout(statusResetTimerRef.current);
    statusResetTimerRef.current = setTimeout(() => setSaveStatus('idle'), 2000);
  }, []);

  const markError = useCallback(() => {
    setSaveStatus('error');
    if (statusResetTimerRef.current) clearTimeout(statusResetTimerRef.current);
    statusResetTimerRef.current = setTimeout(() => setSaveStatus('idle'), 2500);
  }, []);

  const sessionKey = accountId ? buildSessionKey(strategyId, accountId) : strategyId;

  // ─── Build session payload ───────────────────────────────

  const buildSessionData = useCallback((data: AutoChinWorkspaceData) => ({
    type: 'autochinbot',
    writerId: getDeviceWriterId(),
    selectedAccountId: data.selectedAccountId,
    wasRunning: true,
    lastActive: Date.now(),
    workspace: {
      selectedListId: data.selectedListId,
      statusMap: data.statusMap,
      symbolConfigs: data.symbolConfigs,
      pendingOrdersPaused: data.pendingOrdersPaused === true,
      pendingOrdersPauseReason: data.pendingOrdersPauseReason || '',
    },
  }), []);

  // ─── Save workspace to Supabase ──────────────────────────

  const saveWorkspace = useCallback(async (data: AutoChinWorkspaceData) => {
    latestDataRef.current = data;
    setSaveStatus('syncing');

    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const user = sessionData.session?.user;
      if (!user || !sessionData.session) {
        setSaveStatus('idle');
        return;
      }

      authCache = { userId: user.id, accessToken: sessionData.session.access_token };

      await supabase
        .from('bot_session_state')
        .upsert({
          user_id: user.id,
          strategy_id: sessionKey,
          session_data: buildSessionData(data) as unknown as Record<string, unknown>,
          updated_at: new Date().toISOString(),
        }, { onConflict: 'user_id,strategy_id' });
      markSaved();
    } catch (err) {
      console.warn('[AutoChinWorkspace] Failed to save to Supabase:', err);
      markError();
    }
  }, [sessionKey, buildSessionData]);

  // ─── Debounced save ──────────────────────────────────────

  const scheduleSave = useCallback((data: AutoChinWorkspaceData) => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => { saveWorkspace(data); }, 300);
  }, [saveWorkspace]);

  // ─── Immediate save ──────────────────────────────────────

  const saveImmediate = useCallback((data: AutoChinWorkspaceData) => {
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }
    saveWorkspace(data);
  }, [saveWorkspace]);

  // ─── Load workspace from Supabase ────────────────────────

  const loadWorkspace = useCallback(async (): Promise<AutoChinWorkspaceData | null> => {
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const user = sessionData.session?.user;
      if (!user || !sessionData.session) return null;

      authCache = { userId: user.id, accessToken: sessionData.session.access_token };

      let { data, error } = await supabase
        .from('bot_session_state')
        .select('session_data')
        .eq('user_id', user.id)
        .eq('strategy_id', sessionKey)
        .maybeSingle();

      if ((error || !data?.session_data) && accountId) {
        const fallback = await supabase
          .from('bot_session_state')
          .select('session_data')
          .eq('user_id', user.id)
          .eq('strategy_id', strategyId)
          .maybeSingle();

        if (!fallback.error && fallback.data?.session_data) {
          data = fallback.data;
          error = fallback.error;
        }
      }

      if (error || !data?.session_data) return null;

      const raw = data.session_data as Record<string, unknown>;
      if (raw.type !== 'autochinbot') return null;

      const ws = raw.workspace as Record<string, unknown> | undefined;
      const statusMapRaw = (ws?.statusMap as Record<string, string>) || {};
      const statusMap: Record<number, string> = {};
      Object.entries(statusMapRaw).forEach(([k, v]) => { statusMap[Number(k)] = v; });

      return {
        selectedListId: (ws?.selectedListId as string) || '',
        selectedAccountId: (raw.selectedAccountId as string) || '',
        statusMap,
        symbolConfigs: (ws?.symbolConfigs as Record<number, AutoChinSymbolConfig>) || {},
        pendingOrdersPaused: ws?.pendingOrdersPaused === true,
        pendingOrdersPauseReason: (ws?.pendingOrdersPauseReason as string) || '',
      };
    } catch {
      return null;
    }
  }, [sessionKey, strategyId, accountId]);

  // ─── Cancel pending save ─────────────────────────────────

  const cancelPendingSave = useCallback(() => {
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }
  }, []);

  // ─── Keepalive flush (unload-safe) ───────────────────────

  const flushKeepalive = useCallback(() => {
    const data = latestDataRef.current;
    const auth = authCache;
    if (!data || !auth) return;

    const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL as string | undefined;
    const anonKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY as string | undefined;
    if (!supabaseUrl || !anonKey) return;

    const payload = {
      user_id: auth.userId,
      strategy_id: sessionKey,
      session_data: buildSessionData(data),
      updated_at: new Date().toISOString(),
    };

    try {
      fetch(`${supabaseUrl}/rest/v1/bot_session_state?on_conflict=user_id,strategy_id`, {
        method: 'POST',
        keepalive: true,
        headers: {
          'Content-Type': 'application/json',
          apikey: anonKey,
          Authorization: `Bearer ${auth.accessToken}`,
          Prefer: 'resolution=merge-duplicates',
        },
        body: JSON.stringify(payload),
      }).catch(() => { /* keepalive best-effort */ });
    } catch {
      /* ignore */
    }
  }, [sessionKey, buildSessionData]);

  // ─── Realtime cross-device sync ─────────────────────────

  useEffect(() => {
    let cancelled = false;
    let channel: ReturnType<typeof supabase.channel> | null = null;
    const myWriterId = getDeviceWriterId();

    async function setup() {
      const { data: sessionData } = await supabase.auth.getSession();
      const userId = sessionData.session?.user?.id;
      if (!userId || cancelled) return;

      const handleChange = (payload: {
        new?: { strategy_id?: string; session_data?: Record<string, unknown> };
      }) => {
        const row = payload.new;
        if (!row || row.strategy_id !== sessionKey) return;
        const sd = row.session_data;
        if (!sd || sd.type !== 'autochinbot') return;
        if (sd.writerId === myWriterId) return;

        loadWorkspace().then((data) => {
          if (!cancelled && data) onExternalChangeRef.current?.(data);
        });
      };

      channel = supabase
        .channel(`autochin-ws-sync-${sessionKey}`)
        .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'bot_session_state', filter: `user_id=eq.${userId}` }, handleChange)
        .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'bot_session_state', filter: `user_id=eq.${userId}` }, handleChange)
        .subscribe();
    }

    setup();

    return () => {
      cancelled = true;
      if (channel) supabase.removeChannel(channel);
    };
  }, [sessionKey, loadWorkspace]);

  // ─── Unload flush listeners ──────────────────────────────

  useEffect(() => {
    const onPageHide = () => flushKeepalive();
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') flushKeepalive();
    };
    window.addEventListener('pagehide', onPageHide);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('pagehide', onPageHide);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [flushKeepalive]);

  useEffect(() => {
    return () => {
      if (statusResetTimerRef.current) clearTimeout(statusResetTimerRef.current);
    };
  }, []);

  return {
    saveWorkspace,
    scheduleSave,
    saveImmediate,
    loadWorkspace,
    cancelPendingSave,
    flushKeepalive,
    saveStatus,
  };
}